import React, { useEffect, useMemo, useState } from 'react';
import {
  ISO_WEEKDAYS,
  MAX_VALVE_SCHEDULES,
  browserTimeZone,
  createScheduleId,
  findScheduleConflicts,
  formatNextScheduledEvent,
  formatScheduleDays,
  nextScheduledEvent,
  normalizeValveSchedule,
  normalizeValveSchedules,
  opensOnFollowingDay,
  validateValveSchedule,
} from '../../domain/valveSchedule';
import './ValveSchedules.css';

function newSchedule(timezone) {
  return {
    id: createScheduleId(),
    name: '',
    enabled: true,
    timezone,
    days: ISO_WEEKDAYS.map((day) => day.value),
    open_time: '06:00',
    close_time: '23:00',
  };
}

function scheduleTitle(schedule, index) {
  return schedule.name || `Programación ${index + 1}`;
}

function describeTimes(schedule) {
  if (schedule.open_time && schedule.close_time) {
    return `Cierra ${schedule.close_time} · Abre ${schedule.open_time}${
      opensOnFollowingDay(schedule) ? ' al día siguiente' : ''
    }`;
  }
  if (schedule.open_time) return `Abre ${schedule.open_time}`;
  if (schedule.close_time) return `Cierra ${schedule.close_time}`;
  return 'Sin acciones configuradas';
}

function scheduleDaysLabel(schedule) {
  const action = schedule.open_time && schedule.close_time
    ? 'El ciclo inicia'
    : schedule.open_time
      ? 'Abre'
      : 'Cierra';
  return `${action}: ${formatScheduleDays(schedule.days)}`;
}

/** Editor aislado para poder probar la configuración sin accionar la válvula. */
export function ValveSchedules({ schedules: sourceSchedules = [], onSave, disabled = false, disabledReason = null }) {
  const timezone = useMemo(() => browserTimeZone(), []);
  const timezoneOptions = useMemo(() => {
    const supported = typeof Intl.supportedValuesOf === 'function'
      ? Intl.supportedValuesOf('timeZone')
      : [
        'America/Mexico_City', 'America/Cancun', 'America/Chihuahua',
        'America/Hermosillo', 'America/Mazatlan', 'America/Monterrey',
        'America/Tijuana', 'UTC',
      ];
    return [...new Set([timezone, ...supported])].sort();
  }, [timezone]);
  const sourceKey = JSON.stringify(sourceSchedules ?? []);
  const [schedules, setSchedules] = useState(() => normalizeValveSchedules(sourceSchedules, timezone));
  const [draft, setDraft] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    setSchedules(normalizeValveSchedules(sourceSchedules, timezone));
    setDraft(null);
    setErrors({});
    setFeedback(null);
  // `sourceKey` cambia sólo cuando el contenido persistido cambia.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceKey, timezone]);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(timer);
  }, []);

  const nextEvent = useMemo(() => nextScheduledEvent(schedules, now), [schedules, now]);
  const activeCount = schedules.filter((schedule) => schedule.enabled).length;

  const validateList = (candidate) => {
    const invalid = candidate.find((schedule) => !validateValveSchedule(schedule).valid);
    if (invalid) {
      const firstError = Object.values(validateValveSchedule(invalid).errors)[0];
      throw new Error(firstError || 'Hay una programación incompleta.');
    }
    const activeTimeZones = [...new Set(
      candidate.filter((schedule) => schedule.enabled).map((schedule) => schedule.timezone)
    )];
    if (activeTimeZones.length > 1) {
      throw new Error('Todas las programaciones activas del dispositivo deben usar la misma zona horaria.');
    }
    const conflicts = findScheduleConflicts(candidate);
    if (conflicts.length > 0) {
      const conflict = conflicts[0];
      const firstName = conflict.first.schedule.name || 'otra programación';
      throw new Error(`Hay dos programaciones activas a las ${conflict.time} (${firstName}). Cambia la hora o desactiva una.`);
    }
  };

  const persist = async (candidate, successMessage) => {
    if (disabled || saving) return false;
    const normalized = candidate.map((schedule) => normalizeValveSchedule(schedule, timezone));
    try {
      validateList(normalized);
    } catch (error) {
      setFeedback({ type: 'error', message: error.message });
      return false;
    }
    setSaving(true);
    setFeedback({ type: 'pending', message: 'Guardando programaciones…' });
    try {
      await onSave(normalized);
      setSchedules(normalized);
      setFeedback({ type: 'success', message: successMessage });
      return true;
    } catch (error) {
      setFeedback({
        type: 'error',
        message: error?.message || 'No se pudieron guardar las programaciones.',
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const startCreate = () => {
    if (schedules.length >= MAX_VALVE_SCHEDULES) {
      setFeedback({ type: 'error', message: `Puedes guardar hasta ${MAX_VALVE_SCHEDULES} programaciones por dispositivo.` });
      return;
    }
    const deviceTimeZone = schedules.find((schedule) => schedule.enabled)?.timezone
      || schedules[0]?.timezone
      || timezone;
    setDraft(newSchedule(deviceTimeZone));
    setErrors({});
    setFeedback(null);
  };

  const startEdit = (schedule) => {
    setDraft({ ...schedule, days: [...schedule.days] });
    setErrors({});
    setFeedback(null);
  };

  const submitDraft = async (event) => {
    event.preventDefault();
    const validation = validateValveSchedule(draft);
    if (!validation.valid) {
      setErrors(validation.errors);
      return;
    }

    const exists = schedules.some((schedule) => schedule.id === validation.schedule.id);
    const candidate = exists
      ? schedules.map((schedule) => (
        schedule.id === validation.schedule.id ? validation.schedule : schedule
      ))
      : [...schedules, validation.schedule];

    try {
      validateList(candidate);
    } catch (error) {
      setErrors({ form: error.message });
      return;
    }

    if (await persist(candidate, exists ? 'Programación actualizada.' : 'Programación creada.')) {
      setDraft(null);
      setErrors({});
    }
  };

  const toggleSchedule = async (id) => {
    const candidate = schedules.map((schedule) => (
      schedule.id === id ? { ...schedule, enabled: !schedule.enabled } : schedule
    ));
    try {
      await persist(candidate, candidate.find((schedule) => schedule.id === id)?.enabled
        ? 'Programación activada.'
        : 'Programación desactivada.');
    } catch (error) {
      setFeedback({ type: 'error', message: error.message });
    }
  };

  const deleteSchedule = async (id) => {
    const candidate = schedules.filter((schedule) => schedule.id !== id);
    if (await persist(candidate, 'Programación eliminada.') && draft?.id === id) setDraft(null);
  };

  const setDraftField = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined, times: undefined, form: undefined }));
  };

  const toggleDay = (day) => {
    const days = draft.days.includes(day)
      ? draft.days.filter((value) => value !== day)
      : [...draft.days, day].sort((a, b) => a - b);
    setDraftField('days', days);
  };

  return (
    <section className="valve-schedules" aria-labelledby="valve-schedules-title">
      <header className="schedule-header">
        <div>
          <div className="schedule-heading-row">
            <h2 id="valve-schedules-title">Programaciones</h2>
            <span className={`ng-badge ${activeCount ? 'ng-badge--success' : ''}`}>
              {activeCount} {activeCount === 1 ? 'activa' : 'activas'}
            </span>
          </div>
          <p>
            La acción manual se respeta hasta la siguiente hora programada. En un ciclo completo,
            los días indican cuándo cierra; con una sola acción, indican cuándo se ejecuta.
          </p>
        </div>
        {!draft && (
          <button
            type="button"
            className="ng-btn ng-btn--primary"
            onClick={startCreate}
            disabled={saving || disabled || schedules.length >= MAX_VALVE_SCHEDULES}
            title={schedules.length >= MAX_VALVE_SCHEDULES ? `Límite de ${MAX_VALVE_SCHEDULES} programaciones` : undefined}
          >
            Nueva programación
          </button>
        )}
      </header>

      <div className="schedule-next" aria-live="polite">
        <span className="schedule-next-label">Próxima acción</span>
        <strong>{formatNextScheduledEvent(nextEvent)}</strong>
        {nextEvent?.schedule?.name && <span>{nextEvent.schedule.name}</span>}
      </div>

      <p className="schedule-cloud-note">
        Los horarios se ejecutan desde la nube. Si el dispositivo está desconectado en ese minuto,
        la acción vence y no se aplica al reconectarse; así se evitan órdenes atrasadas.
      </p>

      {disabledReason && <div className="schedule-feedback is-pending" role="status">{disabledReason}</div>}

      {feedback && (
        <div
          className={`schedule-feedback is-${feedback.type}`}
          role={feedback.type === 'error' ? 'alert' : 'status'}
        >
          {feedback.message}
        </div>
      )}

      {draft && (
        <form className="schedule-editor" onSubmit={submitDraft} noValidate>
          <div className="schedule-editor-heading">
            <div>
              <h3>{schedules.some((item) => item.id === draft.id) ? 'Editar programación' : 'Nueva programación'}</h3>
              <p>Zona horaria configurada: <code>{draft.timezone}</code></p>
            </div>
            <div className="schedule-enabled-field">
              <span>Activa</span>
              <button
                type="button"
                className="ng-switch"
                role="switch"
                aria-label="Programación activa"
                aria-checked={draft.enabled}
                onClick={() => setDraftField('enabled', !draft.enabled)}
              />
            </div>
          </div>

          <div className="schedule-form-grid">
            <label className="ng-field schedule-name-field">
              <span className="ng-label">Nombre <span className="ng-label__optional">(opcional)</span></span>
              <input
                className="ng-input"
                type="text"
                maxLength="80"
                value={draft.name}
                placeholder="Ej. Cierre nocturno"
                onChange={(event) => setDraftField('name', event.target.value)}
                aria-invalid={Boolean(errors.name)}
              />
              {errors.name && <span className="ng-error-text">{errors.name}</span>}
            </label>

            <div className="ng-field">
              <label className="ng-label" htmlFor={`close-time-${draft.id}`}>Hora de cierre <span className="ng-label__optional">(opcional)</span></label>
              <div className="schedule-time-control">
                <input
                  id={`close-time-${draft.id}`}
                  className="ng-input"
                  type="time"
                  value={draft.close_time}
                  onChange={(event) => setDraftField('close_time', event.target.value)}
                  aria-invalid={Boolean(errors.close_time)}
                />
                {draft.close_time && (
                  <button type="button" className="schedule-clear-time" onClick={() => setDraftField('close_time', '')}>
                    Quitar
                  </button>
                )}
              </div>
              {errors.close_time && <span className="ng-error-text">{errors.close_time}</span>}
            </div>

            <div className="ng-field">
              <label className="ng-label" htmlFor={`open-time-${draft.id}`}>Hora de apertura <span className="ng-label__optional">(opcional)</span></label>
              <div className="schedule-time-control">
                <input
                  id={`open-time-${draft.id}`}
                  className="ng-input"
                  type="time"
                  value={draft.open_time}
                  onChange={(event) => setDraftField('open_time', event.target.value)}
                  aria-invalid={Boolean(errors.open_time)}
                />
                {draft.open_time && (
                  <button type="button" className="schedule-clear-time" onClick={() => setDraftField('open_time', '')}>
                    Quitar
                  </button>
                )}
              </div>
              {errors.open_time && <span className="ng-error-text">{errors.open_time}</span>}
            </div>

            <label className="ng-field schedule-timezone-field">
              <span className="ng-label">Zona horaria</span>
              <select
                className="ng-input"
                value={draft.timezone}
                onChange={(event) => setDraftField('timezone', event.target.value)}
                aria-invalid={Boolean(errors.timezone)}
              >
                {timezoneOptions.map((item) => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </select>
              {errors.timezone && <span className="ng-error-text">{errors.timezone}</span>}
            </label>
          </div>

          {errors.times && <div className="ng-error-text schedule-form-error">{errors.times}</div>}

          {draft.open_time && draft.close_time && (
            <div className="schedule-cycle-preview">
              {describeTimes(draft)}. La acción manual no altera este próximo ciclo.
            </div>
          )}

          <fieldset className="schedule-days">
            <div className="schedule-days-heading">
              <legend>Días de ejecución</legend>
              <button
                type="button"
                className="schedule-all-days"
                onClick={() => setDraftField(
                  'days',
                  draft.days.length === 7 ? [] : ISO_WEEKDAYS.map((day) => day.value)
                )}
              >
                {draft.days.length === 7 ? 'Quitar todos' : 'Todos los días'}
              </button>
            </div>
            <div className="schedule-day-options">
              {ISO_WEEKDAYS.map((day) => (
                <label key={day.value} className={draft.days.includes(day.value) ? 'is-selected' : ''}>
                  <input
                    className="ng-check"
                    type="checkbox"
                    checked={draft.days.includes(day.value)}
                    onChange={() => toggleDay(day.value)}
                  />
                  <span aria-hidden="true">{day.short}</span>
                  <span className="sr-only">{day.label}</span>
                </label>
              ))}
            </div>
            {errors.days && <span className="ng-error-text">{errors.days}</span>}
          </fieldset>

          {errors.form && <div className="ng-error-text schedule-form-error" role="alert">{errors.form}</div>}

          <div className="schedule-editor-actions">
            <button
              type="button"
              className="ng-btn ng-btn--ghost"
              onClick={() => { setDraft(null); setErrors({}); }}
              disabled={saving}
            >
              Cancelar
            </button>
            <button type="submit" className="ng-btn ng-btn--primary" disabled={saving || disabled}>
              {saving ? 'Guardando…' : 'Guardar programación'}
            </button>
          </div>
        </form>
      )}

      {!draft && schedules.length === 0 && (
        <div className="schedule-empty">
          <strong>No hay programaciones</strong>
          <p>No hay acciones automáticas configuradas en esta aplicación.</p>
        </div>
      )}

      <div className="schedule-list">
        {schedules.map((schedule, index) => (
          <article className={`schedule-card ${schedule.enabled ? 'is-enabled' : 'is-disabled'}`} key={schedule.id}>
            <div className="schedule-card-main">
              <div className="schedule-card-title">
                <h3>{scheduleTitle(schedule, index)}</h3>
                <span className={`ng-badge ${schedule.enabled ? 'ng-badge--success' : ''}`}>
                  {schedule.enabled ? 'Activa' : 'Desactivada'}
                </span>
              </div>
              <strong className="schedule-times">{describeTimes(schedule)}</strong>
              <span className="schedule-days-summary">{scheduleDaysLabel(schedule)}</span>
              <span className="schedule-timezone">{schedule.timezone}</span>
            </div>

            <div className="schedule-card-actions">
              <div className="schedule-card-switch">
                <span>{schedule.enabled ? 'Activada' : 'Desactivada'}</span>
                <button
                  type="button"
                  role="switch"
                  className="ng-switch"
                  aria-label={`${schedule.enabled ? 'Desactivar' : 'Activar'} ${scheduleTitle(schedule, index)}`}
                  aria-checked={schedule.enabled}
                  onClick={() => toggleSchedule(schedule.id)}
                  disabled={saving || disabled}
                />
              </div>
              <div>
                <button
                  type="button"
                  className="ng-btn ng-btn--secondary ng-btn--sm"
                  onClick={() => startEdit(schedule)}
                  disabled={saving || disabled}
                >
                  Editar
                </button>
                <button
                  type="button"
                  className="ng-btn ng-btn--danger-quiet ng-btn--sm"
                  onClick={() => deleteSchedule(schedule.id)}
                  disabled={saving || disabled}
                >
                  Eliminar
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

export default ValveSchedules;
